import { useState } from 'react';
import { Item } from 'shared-types';
import moment from 'moment';

import { BlockSelectField } from '@common/components/FormElements';
import { FulfilableBasketItem } from '@features/AddedToBasket/Fulfilable/FulfilableAddedToBasket.types';
import { useTranslations } from '@lang';
import { getItineraryItems } from '@utils/getItineraryView';
import { useLabels } from '@features/Menu/useLabels';

import { AddedToBasketBanner } from '../BasketDrawerBanner/AddedToBasketBanner';
import {
  BasketBannerContainer,
  BasketListingContainer,
  FlexOne,
  GuestSelectContainer,
  ItineraryGrid,
  ItineraryPrimaryText,
  ItinerarySecondaryText,
  ItineraryStack,
} from '../BasketDrawer.styles';

import { Stack } from '../../Stack';
export const BasketDrawerItinerary = ({
  items,
  showAsGrid = false,
  isBasketDrawer = false,
}: {
  items: Item[] | FulfilableBasketItem[];
  showAsGrid?: boolean;
  isBasketDrawer?: boolean;
}) => {
  const { t } = useTranslations();
  const labels = useLabels();
  const [selectedGuest, setSelectedGuest] = useState<number | null>(null);

  if (!items || !Array.isArray(items)) {
    return null;
  }
  const itineraryItems = getItineraryItems(items, t, labels);
  const guestOptions = itineraryItems
    .filter((guest) => guest.hasItems)
    .map((guest) => ({
      label: t('basket.guestItinerary', { number: guest.guestNumber }),
      value: guest.guestNumber,
    }));
  const activeGuest =
    guestOptions.find((option) => option.value === selectedGuest)?.value ??
    guestOptions[0]?.value ??
    1;

  const guestsToShow = showAsGrid
    ? itineraryItems
    : [itineraryItems.find((item) => item.guestNumber === activeGuest)];

  return (
    <BasketListingContainer>
      <FlexOne>
        {isBasketDrawer && (
          <BasketBannerContainer>
            <AddedToBasketBanner />
          </BasketBannerContainer>
        )}
        {!showAsGrid && (
          <GuestSelectContainer>
            <BlockSelectField
              options={guestOptions}
              value={guestOptions.find((title) => title.value === activeGuest)}
              data-testid={`guest-itinerary-select`}
              useNativeDropdownOnMobile={false}
              onChange={(val) => {
                setSelectedGuest(val.value);
              }}
            />
          </GuestSelectContainer>
        )}
        <ItineraryGrid columns={showAsGrid ? 2 : 1}>
          {guestsToShow.map((guestItinerary) => (
            <div key={guestItinerary?.guestNumber}>
              <ItineraryPrimaryText marginTop="20px">
                {guestItinerary?.guestName}
              </ItineraryPrimaryText>
              {showAsGrid && (
                <ItineraryPrimaryText>Guest {guestItinerary?.guestNumber}</ItineraryPrimaryText>
              )}
              <div>
                {Object.entries(guestItinerary?.items || {}).map(([date, items]) => (
                  <Stack alignItems="baseline" gap={0} key={date}>
                    <ItineraryPrimaryText marginBottom="20px" marginTop="20px" color={'#666'}>
                      {moment(date).format('DD MMMM YYYY')}
                    </ItineraryPrimaryText>
                    {items.map((item, index) => (
                      <ItineraryStack
                        direction="horizontal"
                        alignItems="baseline"
                        gap={0}
                        key={`${date}-${index}`}
                      >
                        <ItineraryPrimaryText color={'#666'} data-testid={`itinerary-time`}>
                          {item.time.slice(0, 5)}
                        </ItineraryPrimaryText>
                        <div>
                          <ItineraryPrimaryText color={'#666'}>{item.name}</ItineraryPrimaryText>
                          <ItinerarySecondaryText>{item.parent}</ItinerarySecondaryText>
                        </div>
                      </ItineraryStack>
                    ))}
                  </Stack>
                ))}
              </div>
            </div>
          ))}
        </ItineraryGrid>
      </FlexOne>
    </BasketListingContainer>
  );
};
